<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="modal fade show"
      :class="{ 'd-block': modelValue }"
      tabindex="-1"
      v-bind="$attrs"
      @click.self="handleBackdropClick"
    >
      <div class="modal-dialog" :class="modalSizeClass">
        <div class="modal-content">
          <div v-if="title || $slots['modal-title']" class="modal-header">
            <h5 class="modal-title">
              <slot name="modal-title">{{ title }}</slot>
            </h5>
            <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <slot></slot>
          </div>
          <div v-if="!hideFooter" class="modal-footer">
            <slot name="modal-footer">
              <button v-if="!okOnly" type="button" :class="['btn', `btn-${cancelVariant}`]" @click="close">
                {{ cancelTitle }}
              </button>
              <button type="button" :class="['btn', `btn-${okVariant}`]" @click="handleOk">
                {{ okTitle }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
    <div v-if="modelValue" class="modal-backdrop fade show"></div>
  </teleport>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue'

// Define props
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['', 'sm', 'lg', 'xl'].includes(value)
  },
  okOnly: {
    type: Boolean,
    default: false
  },
  okTitle: {
    type: String,
    default: 'OK'
  },
  cancelTitle: {
    type: String,
    default: 'Cancel'
  },
  hideFooter: {
    type: Boolean,
    default: false
  },
  static: {
    type: Boolean,
    default: false
  },
  okVariant: {
    type: String,
    default: 'primary'
  },
  cancelVariant: {
    type: String,
    default: 'secondary'
  }
})

// Define emits
const emit = defineEmits(['update:modelValue', 'ok', 'cancel', 'hidden'])

// Computed properties
const modalSizeClass = computed(() => {
  if (props.size) {
    return `modal-${props.size}`
  }
  return ''
})

// Watch for modelValue changes to manage body class
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    document.body.classList.add('modal-open')
  } else {
    document.body.classList.remove('modal-open')
    emit('hidden')
  }
})

// Lifecycle cleanup - ensure body class is removed when component unmounts
onUnmounted(() => {
  document.body.classList.remove('modal-open')
})

// Methods
const close = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const handleOk = () => {
  emit('ok')
  if (props.okOnly) {
    close()
  }
}

const handleBackdropClick = () => {
  if (!props.static) {
    close()
  }
}
</script>

<style scoped>
.modal {
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
